import React, { memo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FlexContainer } from '../../../styled/layout.styled';
import checkIcon from './assets/Check.svg';
import arrowDownIcon from './assets/Arrow Down.svg';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import invoiceIcon from './assets/Invoice.svg';

const StyledMainContainer = styled.div`
  position: relative;
`;

const StyledFlexContainer = styled(FlexContainer)`
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: ${({ border }) => (border ? 'solid 1px #c7baf1' : 'solid 1px rgba(199, 186, 241, 0.2)')};
  background-color: #1f0d53;
  transition: all 0.3s ease-in;
  color: #c7baf1;
  position: relative;
  cursor: pointer;
  justify-content: space-between;
  padding: 0 15px 0 0;
  align-items: center;
  ${({ transactionsIcon }) => transactionsIcon
    ? css`
          img {
            width: 16px;
          }
        `
    : css`
   img{
    ${({ trigger }) => trigger
    ? css`
            transform: rotateZ(-180deg);
          `
    : css`
            transform: rotateZ(-360deg);
          `};
    transition: all .3s ease;
  }
}
`}

  p {
    padding: 8px 0 8px 16px;
  }
`;

const StyledLabel = styled.label`
  color: #c7baf1;
  margin-bottom: 8px;
  white-space: nowrap;
`;

const StyledListContainer = styled(FlexContainer)`
  width: 100%;
  overflow: hidden;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 0 4px 20px 0 rgba(13, 4, 40, 0.5);
  background-color: #1f0d53;
  color: #c7baf1;
  position: absolute;
  top: 41px;
  z-index: 1;
  ${({ maxHeight }) => maxHeight
    ? css`
          max-height: 220px;
          overflow-y: ${({ overflowY }) => (overflowY ? 'auto' : 'hidden')};
        `
    : css`
          max-height: 0;
        `};
  transition: max-height 0.3s ease;
`;
const StyledItemWrapper = styled(FlexContainer)`
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ checked }) => (checked ? '#22096c' : 'transparent')};
  transition: all 0.25s ease-in;
  padding: 8px 16px 8px 16px;
  margin-bottom: ${({ margin }) => (margin ? '' : '5px')};
  border-radius: ${({ lastElement }) => lastElement};
`;

const DropDown = ({
  listItems,
  value,
  onCLickHandler,
  label,
  defaultValue,
  trigger,
  setTrigger,
  transactionsIcon,
  overflowY,
}) => {
  const ref = useRef();
  useOnClickOutside(ref, () => setTrigger(false));
  const setTriggerCallBack = useCallback(() => setTrigger(prev => !prev), [setTrigger]);
  return (
    <>
      <FlexContainer ref={ref} width="100%" flexDirection="column">
        {label && <StyledLabel className="small">{label}</StyledLabel>}
        <StyledMainContainer>
          <StyledFlexContainer
            border={trigger}
            trigger={trigger}
            onClick={() => setTriggerCallBack()}
            transactionsIcon={transactionsIcon}
          >
            {defaultValue ? <p className="small">{defaultValue}</p> : <p>{value.name}</p>}
            {transactionsIcon ? <img src={invoiceIcon} alt="icon" /> : <img src={arrowDownIcon} alt="icon" />}
          </StyledFlexContainer>
          <StyledListContainer maxHeight={trigger} overflowY={overflowY}>
            {listItems.map(item => (
              <StyledItemWrapper
                firstElement
                lastElement={
                  (listItems[listItems.length - 1].name === item.name ? '0 0 10px 10px' : '')
                  || (listItems[0].name === item.name ? '10px 10px 0 0' : '')
                }
                margin={listItems[listItems.length - 1].name === item.name}
                checked={value.name === item.name}
                key={item.name}
                onClick={() => onCLickHandler(item)}
              >
                <p className="small" style={{ margin: 0 }}>
                  {item.name}
                </p>
                {value.name === item.name ? <img src={checkIcon} alt="checkIcon" /> : null}
              </StyledItemWrapper>
            ))}
          </StyledListContainer>
        </StyledMainContainer>
      </FlexContainer>
    </>
  );
};

DropDown.propTypes = {
  listItems: PropTypes.array.isRequired,
  value: PropTypes.objectOf(PropTypes.any).isRequired,
  onCLickHandler: PropTypes.func.isRequired,
  trigger: PropTypes.bool.isRequired,
  setTrigger: PropTypes.func.isRequired,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  transactionsIcon: PropTypes.bool,
  overflowY: PropTypes.bool,
};

DropDown.defaultProps = {
  label: '',
  defaultValue: '',
  transactionsIcon: false,
  overflowY: false,
};

export default memo(DropDown);
